import { useState } from 'react';
import type { StepProps } from '../OBWWizard';
import { activePortalApiBaseUrl } from '../../lib/zone-session';
import { BOW_CONTENT } from '../content/bow';
import { countryName, partnerOtherZoneRefusal } from '../residency';

// ADR-073 Amendment 2 (A2.2) — BOW completion bookend. On confirm it provisions
// the participant via the new portal-api route POST /portal/bow/provision
// (T0, HMAC-validated by the BOW launch token), which creates the User + Account
// + Domain, sets the beta flag + Nexa consultant, and emails the activation
// link. Content-driven copy (A2.8 content map). Account-less until this step.

type Phase = 'confirm' | 'submitting' | 'done' | 'error';

interface ProvisionResult {
  ok?: boolean;
  accountId?: string;
  customerNumber?: string;
  emailSent?: boolean;
  error?: string;
}

export function BowCompleteStep({ data, onJumpToStep, onNext }: StepProps) {
  const c = BOW_CONTENT.complete;

  const businessName = ((data?.businessName as string) ?? '').trim();
  const ownerEmail = ((data?.businessEmail as string) ?? '').trim();
  const domainUrl = ((data?.domainUrl as string) ?? '').trim();
  const nexaConsultant = ((data?.nexaConsultant as string) ?? '').trim();
  const bowToken = (data?.bowToken as string) ?? '';
  // ADR-124 — the residency answer (ISO code) and its zone's jurisdiction label, for I9's refusal.
  const jurisdiction = typeof data?.jurisdiction === 'string' ? data.jurisdiction : '';
  const zoneLabel = typeof data?.zoneLabel === 'string' ? data.zoneLabel : '';

  const [phase, setPhase] = useState<Phase>('confirm');
  const [error, setError] = useState('');
  // OBW UAT r2 #7/#4 — when provision fails on a duplicate, the error is no
  // longer a dead end: it deep-links back to the editable field (email lives on
  // the profile step; domain on the domain-verify step).
  const [recover, setRecover] = useState<'email' | 'domain' | 'residency' | null>(null);
  const [result, setResult] = useState<ProvisionResult | null>(null);

  // Client-side completeness check — the participant's email is mandatory to
  // create the Cognito user + send the activation link, but the shared profile
  // step treats it as optional, so we guard here with a clear message.
  const missing: string[] = [];
  if (!businessName) missing.push('business name');
  if (!ownerEmail) missing.push('participant email');
  if (!domainUrl) missing.push('domain');

  async function provision() {
    setError('');
    setRecover(null);
    if (missing.length > 0) {
      setError(`Please go back and complete: ${missing.join(', ')}.`);
      return;
    }
    if (!bowToken) {
      setError('This beta-onboarding session has no launch token. Please re-launch BOW from the dashboard.');
      return;
    }
    setPhase('submitting');
    try {
      const res = await fetch(`${activePortalApiBaseUrl()}/portal/bow/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: bowToken,
          businessName,
          ownerEmail,
          domainUrl,
          nexaConsultant,
          // ⭐ THE PLAN THE OPERATOR CHOSE AT THE `subscribe` STEP (29 July 2026). Sent in the
          // WIZARD's vocabulary — a tier code and a commitment term — and translated to the
          // catalogue product server-side. 'NexaGrowth' has TWO identifiers ('T1' the tier code,
          // 'nexagrowth' the catalogue product id) and they are not interchangeable; keeping the
          // translation in one server-side place is what stops the wrong one being stamped onto
          // an account, where it resolves to null and fails billing closed on a live customer.
          selectedTier: (data?.selectedTier as string) ?? '',
          commitment: (data?.commitment as string) ?? '',
          jurisdiction: (data?.jurisdiction as string) ?? '',
          persona: {
            heroHeadline: data?.heroHeadline ?? '',
            audienceNoun: data?.audienceNoun ?? '',
            placeNoun: data?.placeNoun ?? '',
            funnel: data?.funnel ?? [],
          },
        }),
      });
      const body = (await res.json()) as ProvisionResult;
      if (!res.ok || body.error) {
        if (body.error === 'email_already_exists') {
          setError('A subscriber with that email already exists. Change the email below, or associate the existing user from Customers.');
          setRecover('email');
        } else if (body.error === 'domain_already_registered') {
          setError('That domain is already connected to a Nexanomics account. Change the domain to continue.');
          setRecover('domain');
        } else if (body.error === 'wrong_zone' && jurisdiction && zoneLabel) {
          // ADR-124 D3 — a partner serves only the zone it was created in, so a participant whose
          // country another zone serves cannot be onboarded here; the server refuses (403) before
          // minting anything. I9's approved wording says exactly that, then sends the operator
          // back to the residency step.
          setError(partnerOtherZoneRefusal(countryName(jurisdiction), zoneLabel));
          setRecover('residency');
        } else if (body.error === 'wrong_zone' || body.error === 'invalid_jurisdiction') {
          // An unplaced country, or a journey resumed from before the zone label was recorded:
          // there is no {Zone} to name, so the generic line — and back to the residency step.
          setError('Could not create the beta account. Please try again, or check the participant details.');
          setRecover('residency');
        } else if (body.error === 'token_expired') {
          setError('This beta-onboarding link has expired. Please re-launch BOW from the dashboard.');
        } else {
          setError('Could not create the beta account. Please try again, or check the participant details.');
        }
        setPhase('error');
        return;
      }
      setResult(body);
      setPhase('done');
    } catch {
      setError('Could not reach the server. Please check your connection and try again.');
      setPhase('error');
    }
  }

  if (phase === 'done') {
    return (
      <div className="obw-step-content">
        <div className="obw-ai-status" role="status" style={{ borderColor: 'var(--success)' }}>
          <span className="obw-ai-done" aria-hidden="true">✓</span>
          <span><strong style={{ color: 'var(--text)' }}>{c.successHeading}</strong></span>
        </div>
        <p className="obw-subtitle">{c.successBody}</p>
        <ul className="obw-steps-preview" aria-label="Created">
          <li><span className="obw-step-num" aria-hidden="true">✓</span><span><strong style={{ color: 'var(--text)' }}>{businessName}</strong><br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>{result?.customerNumber ? `Customer № ${result.customerNumber}` : 'Account created'}</span></span></li>
          <li><span className="obw-step-num" aria-hidden="true">✓</span><span><strong style={{ color: 'var(--text)' }}>{domainUrl}</strong><br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>Domain added — awaiting first beacon</span></span></li>
          <li><span className="obw-step-num" aria-hidden="true">{result?.emailSent === false ? '⚠️' : '✓'}</span><span><strong style={{ color: 'var(--text)' }}>{ownerEmail}</strong><br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>{result?.emailSent === false ? 'Account created, but the activation email did not send — resend from the dashboard.' : 'Activation email sent'}</span></span></li>
        </ul>
        {/* L2 (ADR-076 §4) — no window.close(). Completion is emitted to the
            mount via onNext (bow-complete is the last step → OBWWizard fires
            onComplete); the mount dismisses the modal + raises the toast. */}
        <button
          type="button"
          className="obw-btn-primary"
          onClick={() =>
            onNext({
              provisionedAccountId: result?.accountId ?? '',
              provisionedCustomerNumber: result?.customerNumber ?? '',
              activationEmailSent: result?.emailSent !== false,
            })
          }
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">{c.heading}</h2>
      <p className="obw-subtitle">{c.body}</p>

      {/* OBW UAT r2 #2 / ADR-076 — labelled confirmation rows (Participant /
          Domain / Consultant). Previously bare "1"/"2" number badges. */}
      <ul className="obw-confirm-rows" aria-label="Review beta participant">
        <li>
          <span className="obw-confirm-row-label">Participant</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{businessName || '— business name missing —'}</strong>
            <br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>{ownerEmail || '— participant email missing —'}</span>
          </span>
        </li>
        <li>
          <span className="obw-confirm-row-label">Domain</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{domainUrl || '— domain missing —'}</strong>
          </span>
        </li>
        <li>
          <span className="obw-confirm-row-label">Consultant</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{nexaConsultant || '— none recorded —'}</strong>
          </span>
        </li>
      </ul>

      {error && (
        <div className="portal-wizard-error" role="alert">
          {error}
          {recover === 'email' && (
            <div style={{ marginTop: '10px' }}>
              <button type="button" className="obw-btn-secondary" onClick={() => onJumpToStep('profile')}>
                Change email
              </button>
            </div>
          )}
          {recover === 'residency' && (
            <div style={{ marginTop: '10px' }}>
              <button type="button" className="obw-btn-secondary" onClick={() => onJumpToStep('residency')}>
                Change country
              </button>
            </div>
          )}
          {recover === 'domain' && (
            <div style={{ marginTop: '10px' }}>
              <button type="button" className="obw-btn-secondary" onClick={() => onJumpToStep('domain-verify')}>
                Change domain
              </button>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className="obw-btn-primary"
        onClick={() => void provision()}
        disabled={phase === 'submitting'}
      >
        {phase === 'submitting' ? 'Creating…' : c.confirmCta}
      </button>
    </div>
  );
}
